/**
 * Hooks for the Evidence card on the claim detail page.
 *
 * - `useClaimAttachments(claimUuid, includeDeleted)` - lists every
 *   attachment for the claim. Toggle `includeDeleted` to also return
 *   tombstones (rows with `is_deleted=true`).
 *
 * - `useDeleteAttachment()` - soft-delete mutation. The bytes stay on
 *   GCS; the row sticks around with `deleted_at` + `deleted_by` so the
 *   Evidence card can render a tombstone.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import api from '@/admin/lib/api'
import { toastApiError } from '@/shared/lib/api-error'

export interface AttachmentUploader {
  kind: string
  email: string
  name: string
}

export interface Attachment {
  uuid: string
  subject_type: string
  subject_uuid: string
  category: string
  file_url: string | null
  thumbnail_url: string | null
  file_name: string
  file_type: string
  file_size: number
  mime_type: string
  requirement_key: string
  document_option_key: string
  uploader: AttachmentUploader
  uploaded_at: string
  is_deleted: boolean
  deleted_at: string | null
  deleted_by_email: string | null
  deleted_reason: string
}

/**
 * Coerce whatever the attachments query holds into a plain `Attachment[]`.
 *
 * The value can arrive in several shapes: a bare array (the unwrapped
 * envelope), a paginated `{count, next, previous, results}` object, a
 * still-wrapped `{status, message, result}` envelope, or a `{data}` wrapper -
 * and, critically, a STALE/LEGACY shape rehydrated from the persisted
 * IndexedDB cache (the `claim-attachments` key is persisted; see
 * persist-policy.ts). Anything we don't recognise degrades to an empty list
 * rather than throwing - a hostile cached blob must render "No evidence", not
 * crash the card.
 *
 * This lives in `select` (below), NOT in `queryFn`, on purpose: `queryFn` only
 * runs on a network fetch, so a bad value rehydrated from the persisted cache
 * would bypass it and reach `(query.data ?? []).filter(...)` as an object,
 * throwing `filter is not a function` on first paint before the refetch lands.
 * `select` runs on every render against whatever is in cache - fresh OR
 * rehydrated - so it is the only layer that closes the persisted-cache path.
 */
export function normaliseAttachmentListPayload(payload: unknown): Attachment[] {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload as Attachment[]

  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>

    if ('results' in record) {
      return Array.isArray(record.results) ? (record.results as Attachment[]) : []
    }
    if ('result' in record) return normaliseAttachmentListPayload(record.result)
    if ('data' in record) return normaliseAttachmentListPayload(record.data)
  }

  // Unknown/legacy shape (e.g. a stale persisted-cache entry). Degrade to
  // empty instead of throwing so the Evidence card renders its empty state.
  console.warn('Unexpected claim attachments response shape; treating as empty.', payload)
  return []
}

export function useClaimAttachments(
  claimUuid: string | undefined,
  options?: { includeDeleted?: boolean },
) {
  return useQuery<unknown, Error, Attachment[]>({
    queryKey: [
      'claim-attachments',
      claimUuid,
      !!options?.includeDeleted,
    ],
    enabled: !!claimUuid,
    queryFn: async () => {
      const params = options?.includeDeleted ? { include_deleted: 1 } : {}
      const res = await api.get(
        `/files/attachments/claim/${claimUuid}/`,
        { params },
      )
      return res.data
    },
    // Runs on fresh network data AND on values rehydrated from the persisted
    // cache, so both paths are guaranteed to yield an array.
    select: normaliseAttachmentListPayload,
  })
}

/**
 * Upload a supporting document and attach it to a claim with a type tag
 * (e.g. bylaws, proof_of_purchase). Lands in the claim's attachment registry
 * so it appears in the Documentation card.
 */
export function useUploadClaimDocument(claimUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation<
    Attachment,
    unknown,
    { file: File; docType: string }
  >({
    mutationFn: async ({ file, docType }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('use_case', 'CLAIM_EVIDENCE')
      form.append('subject_type', 'claim')
      if (claimUuid) form.append('subject_uuid', claimUuid)
      form.append('requirement_key', docType)
      const res = await api.post('/files/upload/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['claim-attachments', claimUuid] })
    },
  })
}

export interface DocumentSubcategory {
  key: string
  label: string
}

export interface DocumentCategory {
  key: string
  label: string
  /** Claim sections this category's documents surface in (relevance routing). */
  sections?: string[]
  /** Counts toward the "what's still missing" checklist on upload. */
  required?: boolean
  subcategories?: DocumentSubcategory[]
}

/**
 * The tenant's document-category taxonomy - the categories (and any
 * sub-categories) the Evidence uploader offers. Tenant-configurable on
 * the backend (Tenant.settings.claims.document_categories) with a
 * platform default, so the list is never hardcoded in the frontend.
 */
export function useDocumentCategories() {
  return useQuery<DocumentCategory[]>({
    queryKey: ['claim-document-categories'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await api.get('/claims/document-categories/')
      return res.data?.categories ?? []
    },
  })
}

export interface EvidenceFlagCategory {
  key: string
  label: string
}

/**
 * The tenant's evidence flag vocabulary - what a reviewer can say a flagged
 * photo shows. Tenant-configurable on the backend
 * (Tenant.settings.claim_evidence_flag_categories) with a platform default, so
 * the list is never hardcoded in the frontend.
 */
export function useEvidenceFlagCategories() {
  return useQuery<EvidenceFlagCategory[]>({
    queryKey: ['claim-evidence-flag-categories'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await api.get('/claims/evidence-flag-categories/')
      return res.data?.categories ?? []
    },
  })
}

/**
 * Persist a user-created top-level document category to the tenant's taxonomy
 * (Tenant.settings.claims.document_categories) so it is offered on the next
 * upload instead of evaporating. The backend slugifies the label into a stable
 * key and returns the created category plus the refreshed list; we prime the
 * categories cache with that list so the new option appears immediately with
 * its proper label.
 */
export function useCreateDocumentCategory() {
  const qc = useQueryClient()
  return useMutation<
    { category: DocumentCategory; categories: DocumentCategory[] },
    unknown,
    { label: string }
  >({
    mutationFn: async ({ label }) => {
      const res = await api.post('/claims/document-categories/', { label })
      return res.data
    },
    onSuccess: (data) => {
      if (Array.isArray(data?.categories)) {
        qc.setQueryData(['claim-document-categories'], data.categories)
      } else {
        qc.invalidateQueries({ queryKey: ['claim-document-categories'] })
      }
    },
  })
}

/**
 * Upload a piece of evidence and attach it to a claim with a category
 * (main), an optional sub-category, and an optional checklist-item
 * association. Category lands on `UserUploadedFile.category`; the
 * sub-category on `document_option_key`; the checklist item's requirement
 * key on `requirement_key`, which is what the claim's Review Checklist
 * matches on (`resolve_claim_checklist` marks an item satisfied when an
 * upload's `requirement_key`/`category`/`document_option_key` equals the
 * requirement's key/category). Surfaces in the Evidence card immediately
 * via the attachment registry.
 */
export function useUploadClaimEvidence(claimUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation<
    Attachment,
    unknown,
    { file: File; category: string; subcategory?: string; requirementKey?: string }
  >({
    mutationFn: async ({ file, category, subcategory, requirementKey }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('use_case', 'CLAIM_EVIDENCE')
      form.append('subject_type', 'claim')
      if (claimUuid) form.append('subject_uuid', claimUuid)
      form.append('category', category)
      if (subcategory) form.append('document_option_key', subcategory)
      if (requirementKey) form.append('requirement_key', requirementKey)
      const res = await api.post('/files/upload/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['claim-attachments', claimUuid] })
      // An upload mapped to a checklist item flips that item to satisfied, so
      // refresh the claim checklist for the live Review Checklist / progress.
      qc.invalidateQueries({ queryKey: ['claim-checklist', claimUuid] })
    },
  })
}

/**
 * Move an already-uploaded attachment to a different category (WPS-140 #2).
 *
 * Also refreshes the claim's checklist: the category decides which requirement
 * a file satisfies, so moving one can tick a requirement or un-tick it.
 */
export function useRecategoriseAttachment(claimUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation<Attachment, unknown, { uuid: string; category: string }>({
    mutationFn: async ({ uuid, category }) => {
      const res = await api.patch(`/files/attachments/${uuid}/category/`, {
        category,
        // Names the claim for a file answering one of its form fields, which
        // carries no subject of its own. Ignored for every other attachment.
        subject_type: 'claim',
        subject_uuid: claimUuid,
      })
      return res.data
    },
    onSuccess: () => {
      toast.success('Category updated.')
      qc.invalidateQueries({ queryKey: ['claim-attachments', claimUuid] })
      qc.invalidateQueries({ queryKey: ['claim-checklist', claimUuid] })
      // A field file's category reaches the Evidence tab on the claim itself.
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
    },
    onError: (err) => {
      toastApiError(err, "Couldn't change the category.")
    },
  })
}

export function useDeleteAttachment(claimUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation<
    Attachment,
    unknown,
    { uuid: string; reason?: string }
  >({
    mutationFn: async ({ uuid, reason }) => {
      const res = await api.post(`/files/attachments/${uuid}/delete/`, {
        reason: reason ?? '',
      })
      return res.data
    },
    onSuccess: () => {
      toast.success('Attachment deleted.')
      qc.invalidateQueries({ queryKey: ['claim-attachments', claimUuid] })
    },
    onError: () => {
      toast.error("Couldn't delete the attachment.")
    },
  })
}
